import { useMemo, useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { loadList, upsertList } from '../../lib/localList';
import type { Appeal, AppealOutcome, AppealSourceModule, OverdueAppeals } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const OVERDUE_READ_ROLES = ['APPEALS_SECRETARY', 'DISTRICT_MANAGER', 'ASP_ADMIN'];
// Shared institutional cache (not per-persona) — the secretary schedules a
// hearing, the panel chair decides it, both from the same browser session.
const CACHE_KEY = 'cases.staffappeals';

/**
 * S08 exposes only the *overdue* appeals queue (not the full docket) and no
 * `GET /appeals/:id`. This view merges that real, live overdue list with a
 * local cache of appeals this browser has already acted on (acknowledge/
 * schedule/decide all return the updated Appeal) so a case stays visible
 * through its lifecycle even once it's no longer overdue.
 */
export function StaffAppeals() {
  const { persona } = useAuth();
  const isSecretary = persona?.roles.includes('APPEALS_SECRETARY') ?? false;
  const isPanel = persona?.roles.includes('APPEALS_PANEL') ?? false;
  const canReadOverdue = persona?.roles.some((r) => OVERDUE_READ_ROLES.includes(r)) ?? false;

  const [cache, setCache] = useState<Appeal[]>(() => loadList<Appeal>(CACHE_KEY));
  const [showLodge, setShowLodge] = useState(false);
  const [scheduleTarget, setScheduleTarget] = useState<Appeal | null>(null);
  const [decideTarget, setDecideTarget] = useState<Appeal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const overdueQ = useApiQuery<OverdueAppeals>(canReadOverdue ? '/api/v1/cases/appeals/overdue' : null);

  const appeals = useMemo(() => {
    const byId = new Map<string, Appeal>();
    for (const a of cache) byId.set(a.id, a);
    for (const a of overdueQ.data?.acknowledgementOverdue ?? []) byId.set(a.id, a);
    for (const a of overdueQ.data?.decisionOverdue ?? []) byId.set(a.id, a);
    return [...byId.values()].sort((a, b) => b.lodgedAt.localeCompare(a.lodgedAt));
  }, [cache, overdueQ.data]);

  if (!isSecretary && !isPanel && !canReadOverdue) {
    return (
      <div>
        <PageHeader title="Appeals" />
        <ErrorNote message="This view requires the Appeals Secretary, Appeals Tribunal Panel, or District Manager role." />
      </div>
    );
  }

  const upsert = (a: Appeal) => setCache(upsertList(CACHE_KEY, a));

  const acknowledge = async (a: Appeal) => {
    setBusyId(a.id);
    setError(null);
    try {
      const updated = await api.post<Appeal>(`/api/v1/cases/appeals/${a.id}/acknowledge`);
      upsert({ ...a, ...updated });
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Social Protection Appeals Tribunal"
        subtitle="Statutory clock: acknowledge within 7 days, decide within 60 days of lodging"
        actions={
          isSecretary ? (
            <button className="btn-primary" onClick={() => setShowLodge(true)}>
              + Lodge appeal (on behalf)
            </button>
          ) : undefined
        }
      />
      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {overdueQ.error ? <ErrorNote message={overdueQ.error} /> : null}

      {overdueQ.loading ? <Spinner /> : null}

      <div className="card overflow-x-auto">
        {appeals.length === 0 ? (
          <EmptyState title="No appeals to work" hint="Overdue appeals appear here automatically; appeals you act on stay visible through their lifecycle." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Reference</th>
                <th className="table-head">NIN</th>
                <th className="table-head">Against</th>
                <th className="table-head">Status</th>
                <th className="table-head">Ack. due</th>
                <th className="table-head">Decision due</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {appeals.map((a) => (
                <tr key={a.id}>
                  <td className="table-cell font-medium">{a.refNumber}</td>
                  <td className="table-cell text-xs">{a.nin}</td>
                  <td className="table-cell text-xs">
                    {a.sourceModule.toUpperCase()} · {a.sourceDecisionRef}
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={a.status} />
                    {a.outcome ? (
                      <div className="mt-1">
                        <StatusBadge status={a.outcome} />
                      </div>
                    ) : null}
                  </td>
                  <td className="table-cell text-xs text-slate-500">{formatDate(a.ackDueAt)}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDate(a.decisionDueAt)}</td>
                  <td className="table-cell text-right">
                    {isSecretary && a.status === 'LODGED' ? (
                      <button className="btn-secondary !px-2 !py-1 text-xs" onClick={() => acknowledge(a)} disabled={busyId === a.id}>
                        Acknowledge
                      </button>
                    ) : isSecretary && a.status === 'ACKNOWLEDGED' ? (
                      <button className="btn-secondary !px-2 !py-1 text-xs" onClick={() => setScheduleTarget(a)}>
                        Schedule hearing
                      </button>
                    ) : isPanel && a.status === 'HEARING' ? (
                      <button className="btn-primary !px-2 !py-1 text-xs" onClick={() => setDecideTarget(a)}>
                        Decide
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showLodge ? (
        <LodgeModal
          onClose={() => setShowLodge(false)}
          onDone={(a) => {
            upsert(a);
            setShowLodge(false);
          }}
        />
      ) : null}
      {scheduleTarget ? (
        <ScheduleModal
          appeal={scheduleTarget}
          onClose={() => setScheduleTarget(null)}
          onDone={(a) => {
            upsert(a);
            setScheduleTarget(null);
          }}
        />
      ) : null}
      {decideTarget ? (
        <DecideModal
          appeal={decideTarget}
          onClose={() => setDecideTarget(null)}
          onDone={(a) => {
            upsert(a);
            setDecideTarget(null);
          }}
        />
      ) : null}
    </div>
  );
}

function LodgeModal({ onClose, onDone }: { onClose: () => void; onDone: (a: Appeal) => void }) {
  const [nin, setNin] = useState('');
  const [sourceModule, setSourceModule] = useState<AppealSourceModule>('s02');
  const [sourceDecisionRef, setSourceDecisionRef] = useState('');
  const [grounds, setGrounds] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const appeal = await api.post<Appeal>('/api/v1/cases/appeals', {
        nin,
        sourceModule,
        sourceDecisionRef,
        grounds,
        lodgedAt: new Date().toISOString().slice(0, 10),
      });
      onDone(appeal);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not lodge appeal');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Lodge appeal on behalf of a claimant" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Claimant NIN">
          <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} />
        </Field>
        <Field label="Decision being appealed">
          <select className="field-input" value={sourceModule} onChange={(e) => setSourceModule(e.target.value as AppealSourceModule)}>
            <option value="s02">Statutory benefit decision (S02)</option>
            <option value="s03">Welfare assistance decision (S03)</option>
          </select>
        </Field>
        <Field label="Claim / application reference">
          <input className="field-input" value={sourceDecisionRef} onChange={(e) => setSourceDecisionRef(e.target.value)} />
        </Field>
        <Field label="Grounds">
          <textarea className="field-input" rows={4} value={grounds} onChange={(e) => setGrounds(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !nin || !sourceDecisionRef || grounds.length < 10}>
            {busy ? 'Lodging…' : 'Lodge'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ScheduleModal({ appeal, onClose, onDone }: { appeal: Appeal; onClose: () => void; onDone: (a: Appeal) => void }) {
  const [hearingDate, setHearingDate] = useState('');
  const [panel, setPanel] = useState('Terrence Rath:Chair, Ginette Moustache:Secretary, Yannick Confait:Member');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.post<Appeal>(`/api/v1/cases/appeals/${appeal.id}/schedule`, {
        hearingDate,
        panel: panel
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .map((s) => {
            const [name, role] = s.split(':').map((x) => x.trim());
            return { name: name ?? s, role: role ?? 'Member' };
          }),
      });
      onDone({ ...appeal, ...updated });
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not schedule hearing');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Schedule hearing — ${appeal.refNumber}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Hearing date">
          <input className="field-input" type="date" value={hearingDate} onChange={(e) => setHearingDate(e.target.value)} />
        </Field>
        <Field label="Panel (name:role, comma-separated — at least 3)">
          <input className="field-input" value={panel} onChange={(e) => setPanel(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !hearingDate}>
            {busy ? 'Scheduling…' : 'Schedule'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DecideModal({ appeal, onClose, onDone }: { appeal: Appeal; onClose: () => void; onDone: (a: Appeal) => void }) {
  const [outcome, setOutcome] = useState<AppealOutcome>('UPHELD');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.post<Appeal>(`/api/v1/cases/appeals/${appeal.id}/decide`, { outcome, note });
      onDone({ ...appeal, ...updated });
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not record decision');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Tribunal decision — ${appeal.refNumber}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Outcome">
          <select className="field-input" value={outcome} onChange={(e) => setOutcome(e.target.value as AppealOutcome)}>
            <option value="UPHELD">Upheld — original decision stands</option>
            <option value="OVERTURNED">Overturned</option>
            <option value="PARTIALLY_OVERTURNED">Partially overturned</option>
          </select>
        </Field>
        <Field label="Reasons">
          <textarea className="field-input" rows={4} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {outcome !== 'UPHELD' ? (
          <p className="text-xs text-amber-600">This automatically adjusts the underlying benefit/welfare decision.</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || note.length < 5}>
            {busy ? 'Saving…' : 'Record decision'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
